import { api } from "./api";

export interface ForumAuthor {
  user: string;
  name: string;
  avatar?: string;
  role: string;
  posts: number;
}

export interface Forum {
  _id: string;
  id: string;
  title: string;
  description: string;
  course: string;
  courseTitle: string;
  courseThumbnail?: string;
  category: string;
  isActive: boolean;
  requireModeration: boolean;
  instructor: ForumAuthor;
  members: string[];
  moderators: string[];
  memberCount: number;
  topics: number;
  posts: number;
  isJoined?: boolean;
  notificationSettings: {
    isNotifying: boolean;
    emailNotifications: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Discussion {
  _id: string;
  id: string;
  forum: string;
  title: string;
  content: string;
  author: {
    user: string;
    name: string;
    avatar?: string;
    role: string;
    badge?: string;
    posts: number;
  };
  category: string;
  tags: string[];
  views: number;
  likes: number;
  likedBy: string[];
  isPinned: boolean;
  isLocked: boolean;
  isHot: boolean;
  isResolved: boolean;
  replyCount: number;
  lastActivity: string;
  createdAt: string;
  updatedAt: string;
  replies?: Reply[];
}

export interface Reply {
  _id: string;
  id: string;
  author: {
    user: string;
    name: string;
    avatar?: string;
    role: string;
    badge?: string;
    posts: number;
  };
  content: string;
  likes: number;
  likedBy: string[];
  isSolution: boolean;
  isApproved: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ForumStats {
  totalTopics: number;
  totalPosts: number;
  totalMembers: number;
  activeToday: number;
  newToday: number;
}

export const forumApi = {
  // Get forum by course ID
  getByCourse: (courseId: string) => {
    return api.get<{ success: boolean; data: Forum }>(`/forums/course/${courseId}`);
  },

  // Get forum by ID
  getById: (forumId: string) => {
    return api.get<{ success: boolean; data: Forum }>(`/forums/${forumId}`);
  },

  // Get forum with discussions
  getWithDiscussions: (forumId: string, options?: { page?: number; limit?: number; category?: string; sort?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.category) params.append("category", options.category);
    if (options?.sort) params.append("sort", options.sort);
    
    return api.get<{ 
      success: boolean; 
      data: {
        forum: Forum;
        summary: any;
        discussions: Discussion[];
      };
    }>(`/forums/${forumId}/with-discussions${params.toString() ? `?${params.toString()}` : ""}`);
  },

  // Create forum for a course (instructor only)
  createForCourse: (courseId: string, data: { title?: string; description?: string; category?: string }) => {
    return api.post<{ success: boolean; message: string; data: Forum }>(
      `/forums/${courseId}`,
      data
    );
  },

  // Update forum
  update: (forumId: string, data: { title?: string; description?: string; category?: string; isActive?: boolean }) => {
    return api.put<{ success: boolean; message: string; data: Forum }>(
      `/forums/${forumId}`,
      data
    );
  },

  // Delete forum
  delete: (forumId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/forums/${forumId}`);
  },

  // Get forum members
  getMembers: (forumId: string, options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    
    return api.get<{ success: boolean; data: any[]; pagination: any }>(
      `/forums/${forumId}/members${params.toString() ? `?${params.toString()}` : ""}`
    );
  },

  // Get forum moderators
  getModerators: (forumId: string) => {
    return api.get<{ success: boolean; data: any[] }>(`/forums/${forumId}/moderators`);
  },

  // Get forum stats
  getStats: (forumId: string) => {
    return api.get<{ success: boolean; data: ForumStats }>(`/forums/${forumId}/stats`);
  },

  // Add member to forum
  addMember: (forumId: string, userId: string) => {
    return api.post<{ success: boolean; message: string }>(`/forums/${forumId}/members/${userId}`, {});
  },

  // Join forum (self)
  joinForum: (forumId: string) => {
    return api.post<{ success: boolean; message: string }>(`/forums/${forumId}/join`, {});
  },

  // Leave forum (self)
  leaveForum: (forumId: string) => {
    return api.post<{ success: boolean; message: string }>(`/forums/${forumId}/leave`, {});
  },

  // Remove member from forum
  removeMember: (forumId: string, userId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/forums/${forumId}/members/${userId}`);
  },

  // Add moderator
  addModerator: (forumId: string, userId: string) => {
    return api.post<{ success: boolean; message: string }>(`/forums/${forumId}/moderators/${userId}`, {});
  },

  // Remove moderator
  removeModerator: (forumId: string, userId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/forums/${forumId}/moderators/${userId}`);
  },

  // Toggle notifications
  toggleNotifications: (forumId: string, isNotifying: boolean) => {
    return api.put<{ success: boolean; message: string }>(`/forums/${forumId}/notifications`, { isNotifying });
  },

  // Get notification status
  getNotificationStatus: (forumId: string) => {
    return api.get<{ success: boolean; data: { isNotifying: boolean } }>(`/forums/${forumId}/notifications/status`);
  },

  // Search discussions
  search: (forumId: string, query: string, options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    params.append("q", query);
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    
    return api.get<{ success: boolean; data: Discussion[]; pagination: any }>(
      `/forums/${forumId}/search?${params.toString()}`
    );
  },

  // Get user's enrolled courses with forums
  getUserEnrolledCoursesWithForums: () => {
    return api.get<{ success: boolean; data: { course: any; forum: Forum | null }[] }>("/forums/user/enrolled-courses");
  },

  // Get available forums (for courses user is not enrolled in)
  getAvailableForums: () => {
    return api.get<{ success: boolean; data: Forum[] }>("/forums/available");
  },
};

// Discussion API
export const discussionApi = {
  // Get discussions for a forum
  getByForum: (forumId: string, options?: { page?: number; limit?: number; category?: string; sort?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.category) params.append("category", options.category);
    if (options?.sort) params.append("sort", options.sort);
    
    return api.get<{ 
      success: boolean; 
      count: number;
      data: Discussion[];
      pagination: any;
    }>(`/discussions/forums/${forumId}/discussions${params.toString() ? `?${params.toString()}` : ""}`);
  },

  // Get a single discussion
  getById: (discussionId: string) => {
    return api.get<{ success: boolean; data: Discussion }>(`/discussions/${discussionId}`);
  },

  // Create a discussion
  create: (forumId: string, data: { title: string; content: string; category: string; tags?: string[] }) => {
    return api.post<{ success: boolean; message: string; data: Discussion }>(
      `/discussions/${forumId}/discussions`,
      data
    );
  },

  // Update a discussion
  update: (discussionId: string, data: { title?: string; content?: string; category?: string; tags?: string[] }) => {
    return api.put<{ success: boolean; message: string; data: Discussion }>(
      `/discussions/${discussionId}`,
      data
    );
  },

  // Delete a discussion
  delete: (discussionId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/discussions/${discussionId}`);
  },

  // Add a reply
  addReply: (discussionId: string, content: string) => {
    return api.post<{ success: boolean; message: string; data: Discussion }>(
      `/discussions/${discussionId}/replies`,
      { content }
    );
  },

  // Update a reply
  updateReply: (discussionId: string, replyId: string, content: string) => {
    return api.put<{ success: boolean; message: string; data: Discussion }>(
      `/discussions/${discussionId}/replies/${replyId}`,
      { content }
    );
  },

  // Delete a reply
  deleteReply: (discussionId: string, replyId: string) => {
    return api.delete<{ success: boolean; message: string }>(`/discussions/${discussionId}/replies/${replyId}`);
  },

  // Like/unlike a discussion
  toggleLike: (discussionId: string) => {
    return api.post<{ success: boolean; message: string; data: { likes: number; isLiked: boolean } }>(
      `/discussions/${discussionId}/like`,
      {}
    );
  },

  // Bookmark/unbookmark a discussion
  toggleBookmark: (discussionId: string) => {
    return api.post<{ success: boolean; message: string; data: { isBookmarked: boolean } }>(
      `/discussions/${discussionId}/bookmark`,
      {}
    );
  },

  // Like/unlike a reply
  toggleReplyLike: (discussionId: string, replyId: string) => {
    return api.post<{ success: boolean; message: string; data: { likes: number; isLiked: boolean } }>(
      `/discussions/${discussionId}/replies/${replyId}/like`,
      {}
    );
  },

  // Mark a reply as solution
  markSolution: (discussionId: string, replyId: string) => {
    return api.put<{ success: boolean; message: string; data: Discussion }>(
      `/discussions/${discussionId}/solution/${replyId}`,
      {}
    );
  },

  // Pin/unpin discussion (moderator)
  togglePin: (discussionId: string) => {
    return api.put<{ success: boolean; message: string; data: Discussion }>(
      `/discussions/${discussionId}/pin`,
      {}
    );
  },

  // Lock/unlock discussion (moderator)
  toggleLock: (discussionId: string) => {
    return api.put<{ success: boolean; message: string; data: Discussion }>(
      `/discussions/${discussionId}/lock`,
      {}
    );
  },

  // Get user's discussions
  getUserDiscussions: (userId: string, options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    
    return api.get<{ 
      success: boolean; 
      count: number;
      data: Discussion[];
      pagination: any;
    }>(`/discussions/users/${userId}/discussions${params.toString() ? `?${params.toString()}` : ""}`);
  },

  // Get bookmarked discussions
  getBookmarked: (options?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    
    return api.get<{ 
      success: boolean; 
      count: number;
      data: Discussion[];
      pagination: any;
    }>(`/discussions/users/bookmarks${params.toString() ? `?${params.toString()}` : ""}`);
  },
};